import { Info, Sparkles } from "lucide-react";

import {
  CosmosQueryFileSchema,
  CosmosQueryResultItemSchema,
  CosmosSourceSchema,
} from "@api/generated/types.gen";

import { FileIcon } from "@components/ui/file-icon";
import { Skeleton } from "@components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { linkSources } from "../utils/linkSources";
import { parseAnswer } from "../utils/parseAnswer";

const AI_DISCLAIMER =
  "AI-generated content may contain inaccuracies. Always verify against the case documents.";

type SearchAnswerProps = {
  answer: string;
  isLoading: boolean;
  results?: CosmosQueryResultItemSchema[];
  sources?: CosmosSourceSchema[];
  onOpenSource?: (
    file: CosmosQueryFileSchema,
    page: number,
    snippet?: string,
    bboxes?: CosmosSourceSchema["bboxes"]
  ) => void;
};

export const SearchAnswer = ({
  answer,
  isLoading,
  results = [],
  sources = [],
  onOpenSource,
}: SearchAnswerProps) => {
  const blocks = parseAnswer(answer);

  // Render a run of prose, turning inline [n] citation markers (or, as a
  // fallback, verbatim source file names) into citation chips.
  const renderText = (text: string) =>
    linkSources(text, results, sources).map((segment, i) => {
      if (segment.type === "text") return segment.text;

      const chipClass =
        "mx-0.5 inline-flex items-center gap-0.5 rounded bg-primary-50 px-1 py-0.5 align-baseline text-[10px] font-medium text-primary";

      // File-type icon (PDF/Word/generic) keyed off the resolved file name;
      // marker chips with no resolved file fall back to the generic glyph.
      const file = segment.file;
      const icon = (
        <FileIcon fileName={file?.name ?? ""} className="size-3.5 shrink-0" />
      );

      // Interactive chip only when we can both open a source and resolve the
      // file; otherwise a plain (still readable) label so it doesn't drop out
      // of the a11y tree.
      if (!onOpenSource || !file) {
        return (
          <span key={i} className={chipClass}>
            {icon}
            {segment.label}
          </span>
        );
      }

      return (
        <button
          key={i}
          type="button"
          onClick={() =>
            onOpenSource(file, segment.page, segment.snippet, segment.bboxes)
          }
          className={chipClass}
        >
          {icon}
          {segment.label}
        </button>
      );
    });

  return (
    <div>
      <div className="flex items-center gap-2 p-4">
        <Sparkles className="size-[18px] text-primary" />
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] font-semibold text-foreground">
            AI Answer
          </span>
          <Tooltip>
            <TooltipTrigger
              type="button"
              aria-label="About AI-generated content"
              className="inline-flex items-center text-foreground-muted hover:text-foreground"
            >
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{AI_DISCLAIMER}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2.5 px-4 pb-[18px]">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-4 pb-[18px] text-xs leading-[1.65] text-foreground">
          {blocks.map((block, index) => {
            if (block.type === "heading") {
              return (
                <h4 key={index} className="font-semibold text-foreground">
                  {block.text}
                </h4>
              );
            }
            if (block.type === "ordered-list") {
              return (
                <ol key={index} className="list-decimal space-y-1 ps-[18px]">
                  {block.items.map((item, i) => (
                    <li key={i}>{renderText(item)}</li>
                  ))}
                </ol>
              );
            }
            return (
              <p key={index} className="whitespace-pre-line">
                {renderText(block.text)}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
};
